import {
  MarketingTriggerKey,
  OrderTrackingTriggerKey,
} from "@redotech/redo-model/advanced-flow/triggers";
import { ShopperAgentAutomationConfigSchema } from "@redotech/redo-model/shopper-agent/automation-config";
import { ShopperAgentCooldownConfigSchema } from "@redotech/redo-model/shopper-agent/cooldown-config";
import { ShopperAgentDiscountConfigSchema } from "@redotech/redo-model/shopper-agent/discount-config";
import { ShopperAgentIMessageConfigSchema } from "@redotech/redo-model/shopper-agent/imessage-config";
import { ShopperAgentReplyConfigSchema } from "@redotech/redo-model/shopper-agent/reply-config";
import { SkillConfigSchema } from "@redotech/redo-model/shopper-agent/skill-config";
import { ShopperAgentTriggerSkipConditionsSchema } from "@redotech/redo-model/shopper-agent/trigger-config";
import { getTriggerById } from "@redotech/redo-model/shopper-agent/triggers";
import * as z from "zod/v4";

/**
 * Input schema for updating a trigger.
 *
 * - Pass `config: null` to remove the entire trigger override.
 * - Within config, pass a field as `null` to remove that specific override
 *   (e.g. `discountConfig: null` removes the discount override so it inherits
 *   from base). Pass `undefined`/omit to leave unchanged.
 */
const TriggerUpdateConfigSchema = z.object({
  enabled: z.boolean().optional(),
  iMessageConfig: ShopperAgentIMessageConfigSchema.nullable().optional(),
  replyConfig: ShopperAgentReplyConfigSchema.nullable().optional(),
  discountConfig: ShopperAgentDiscountConfigSchema.nullable().optional(),
  cooldownConfig: ShopperAgentCooldownConfigSchema.nullable().optional(),
  automations: z
    .array(ShopperAgentAutomationConfigSchema)
    .nullable()
    .optional(),
  skills: z.record(z.string(), SkillConfigSchema).nullable().optional(),
  guidance: z.string().nullable().optional(),
  skipConditions: ShopperAgentTriggerSkipConditionsSchema.nullable().optional(),
});

const TriggerIdSchema = z.enum([
  ...Object.values(MarketingTriggerKey),
  ...Object.values(OrderTrackingTriggerKey),
]);

export const updateShopperAgentTrigger = {
  name: "updateShopperAgentTrigger" as const,
  input: z
    .object({
      triggerId: TriggerIdSchema,
      config: TriggerUpdateConfigSchema.nullable(),
    })
    .refine(
      ({ triggerId, config }) => {
        if (!config) return true;
        const automations = config.automations;
        if (!automations || automations.length === 0) return true;
        const def = getTriggerById(triggerId);
        // `supportsAutomations` is `false` only for triggers whose advanced
        // flows must always fire outside the shopper agent (e.g. SMS_SIGNUP).
        return def?.supportsAutomations !== false;
      },
      {
        error:
          "This trigger does not support attaching automations — its flows are dispatched outside the shopper agent.",
        path: ["config", "automations"],
      },
    )
    .refine(
      ({ triggerId, config }) => {
        if (!config) return true;
        const skipConditions = config.skipConditions;
        if (!skipConditions || skipConditions.conditions.length === 0) {
          return true;
        }
        const def = getTriggerById(triggerId);
        // `supportsSkipConditions` is `false` for triggers where the
        // coordinator handoff happens without a SchemaInstance in scope
        // (today: SMS_SIGNUP). Reject saves so merchants can't configure
        // a state that would never be evaluated.
        return def?.supportsSkipConditions !== false;
      },
      {
        error:
          "This trigger does not support skip conditions — its routing happens outside the path where conditions are evaluated.",
        path: ["config", "skipConditions"],
      },
    ),
  output: z.object({ success: z.boolean() }),
};
